import { useCallback, useState } from 'react'
import { lobbyAttendeeApi } from '../lib/api'
import { useGrantRedeem } from './useGrantRedeem'
import type { AttendeeProfileDto, UpdateAttendeeProfileRequest } from '../types/lobbyDay'

/**
 * Attendee self-profile read/edit seam. Reuses useGrantRedeem: a load or save first ensures a
 * grant_session (which may surface termsRequired -> TermsAckPanel), then GET/PUTs the profile.
 * A pending terms-ack is completed via acceptTerms(next), which re-redeems with acceptTerms=true
 * and then performs the originally-requested load or save.
 */
export function useAttendeeProfile(token: string | undefined) {
  const redeem = useGrantRedeem(token)
  const [profile, setProfile] = useState<AttendeeProfileDto | undefined>(undefined)
  const [isBusy, setIsBusy] = useState(false)

  const doLoad = useCallback(async () => {
    const p = await lobbyAttendeeApi.getProfile()
    setProfile(p)
  }, [])

  const doSave = useCallback(async (patch: UpdateAttendeeProfileRequest) => {
    await lobbyAttendeeApi.updateProfile(patch)
    await doLoad()
  }, [doLoad])

  const load = useCallback(async () => {
    setIsBusy(true)
    try {
      if ((await redeem.ensureSession()) === 'ok') await doLoad()
    } finally { setIsBusy(false) }
  }, [redeem.ensureSession, doLoad])

  const save = useCallback(async (patch: UpdateAttendeeProfileRequest) => {
    setIsBusy(true)
    try {
      if ((await redeem.ensureSession()) === 'ok') await doSave(patch)
    } finally { setIsBusy(false) }
  }, [redeem.ensureSession, doSave])

  // Called after the user accepts terms in the panel: re-redeem accepting, then do the deferred op.
  const acceptTerms = useCallback(async (next: 'load' | 'save', patch?: UpdateAttendeeProfileRequest) => {
    setIsBusy(true)
    try {
      if ((await redeem.ensureSession(true)) === 'ok') {
        if (next === 'save' && patch) await doSave(patch)
        else await doLoad()
      }
    } finally { setIsBusy(false) }
  }, [redeem.ensureSession, doLoad, doSave])

  return {
    load, save, acceptTerms, profile,
    termsRequired: redeem.termsRequired,
    unavailable: redeem.unavailable,
    isBusy: isBusy || redeem.isRedeeming,
  }
}
